import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import { finance } from '../../services/apiClient';

function formatMoney(value) {
  return `R$ ${Number(value || 0).toFixed(2)}`;
}

function formatSchedule(value) {
  const key = String(value || '').toLowerCase();
  if (key === 'daily') return 'Diario';
  if (key === 'monthly') return 'Mensal';
  return 'Semanal';
}

export default function AdminFinance() {
  const navigate = useNavigate();
  const [account, setAccount] = useState({ account_type: 'pix', data: {} });
  const [storesPayouts, setStoresPayouts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({ q: '', status: 'all' });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [settlingStoreIds, setSettlingStoreIds] = useState({});

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [accountResponse, payoutsResponse] = await Promise.all([
        finance.getAccount(),
        finance.getStorePayouts({ q: filters.q || undefined, status: filters.status || undefined }),
      ]);

      setAccount(accountResponse?.account || { account_type: 'pix', data: {} });
      setStoresPayouts(Array.isArray(payoutsResponse?.stores) ? payoutsResponse.stores : []);
      setSummary(payoutsResponse?.summary || null);
    } catch {
      setError('Erro ao carregar financeiro');
    } finally {
      setLoading(false);
    }
  }, [filters.q, filters.status]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await finance.updateAccount(account);
      alert('Dados financeiros atualizados');
    } catch (err) {
      alert(err?.response?.data?.detail || 'Nao foi possivel salvar os dados financeiros');
    } finally {
      setSaving(false);
    }
  };

  const handleSettleStore = async (row) => {
    const pendingAmount = Number(row?.totals?.store_payout_pending || 0);
    if (pendingAmount <= 0) {
      alert('Esta loja nao possui repasse pendente.');
      return;
    }

    const confirmed = window.confirm(
      `Confirmar repasse da loja ${row.store_name} no valor de ${formatMoney(pendingAmount)}?`
    );
    if (!confirmed) return;

    setSettlingStoreIds((prev) => ({ ...prev, [row.store_id]: true }));
    try {
      await finance.settleStorePayout(row.store_id, { scope: 'pending' });
      await loadData();
      alert('Repasse marcado como pago com sucesso.');
    } catch (err) {
      alert(err?.response?.data?.detail || 'Nao foi possivel marcar o repasse.');
    } finally {
      setSettlingStoreIds((prev) => ({ ...prev, [row.store_id]: false }));
    }
  };

  const overviewCards = useMemo(() => {
    return [
      { label: 'Lojas no filtro', value: Number(summary?.stores_total || 0) },
      { label: 'Lojas com repasse pendente', value: Number(summary?.stores_with_pending || 0) },
      { label: 'Pedidos finalizados', value: Number(summary?.completed_orders_count || 0) },
      { label: 'Repasse pendente (R$)', value: formatMoney(summary?.store_payout_pending || 0) },
      { label: 'Repasse pago (R$)', value: formatMoney(summary?.store_payout_paid || 0) },
      { label: 'Frete Correfood excluido (R$)', value: formatMoney(summary?.delivery_fee_platform_excluded || 0) },
    ];
  }, [summary]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="admin-section">
      <PageHeader
        title="Financeiro"
        subtitle="Repasse do admin para lojas"
        actions={(
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="admin-button secondary" onClick={loadData}>Atualizar</button>
            <button className="admin-button" onClick={handleSave} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
          </div>
        )}
      />

      <div className="admin-card">
        <h3>Conta de recebimento</h3>
        <div className="admin-filters">
          <select
            className="admin-select"
            value={account.account_type || 'pix'}
            onChange={(e) => setAccount((prev) => ({ ...prev, account_type: e.target.value }))}
          >
            <option value="pix">Pix</option>
            <option value="bank">Banco</option>
          </select>
          <input
            className="admin-input"
            placeholder="Chave Pix / Conta"
            value={account?.data?.pix_key || ''}
            onChange={(e) => setAccount((prev) => ({ ...prev, data: { ...(prev.data || {}), pix_key: e.target.value } }))}
          />
          <input
            className="admin-input"
            placeholder="Titular"
            value={account?.data?.holder || ''}
            onChange={(e) => setAccount((prev) => ({ ...prev, data: { ...(prev.data || {}), holder: e.target.value } }))}
          />
          <input
            className="admin-input"
            placeholder="Documento"
            value={account?.data?.document || ''}
            onChange={(e) => setAccount((prev) => ({ ...prev, data: { ...(prev.data || {}), document: e.target.value } }))}
          />
        </div>
      </div>

      <div className="admin-grid">
        {overviewCards.map((card) => (
          <div key={card.label} className="admin-card">
            <h3>{card.label}</h3>
            <div className="value">{card.value}</div>
          </div>
        ))}
      </div>

      <div className="admin-card">
        <h3>Repasses por loja</h3>
        <div className="admin-filters" style={{ marginBottom: 12 }}>
          <input
            className="admin-input"
            placeholder="Buscar loja"
            value={filters.q}
            onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value }))}
          />
          <select
            className="admin-select"
            value={filters.status}
            onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
          >
            <option value="all">Todos</option>
            <option value="pending">Com pendencia</option>
            <option value="paid">Somente pagos</option>
          </select>
          <button className="admin-button secondary" onClick={loadData}>Aplicar</button>
        </div>

        <div className="admin-fees-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Loja</th>
                <th>Pedidos finalizados</th>
                <th>Entrega propria</th>
                <th>Entrega Correfood</th>
                <th>A repassar</th>
                <th>Ja repassado</th>
                <th>Periodicidade</th>
                <th>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {storesPayouts.map((row) => {
                const settling = !!settlingStoreIds[row.store_id];
                const pendingValue = Number(row?.totals?.store_payout_pending || 0);
                return (
                  <tr key={row.store_id}>
                    <td>
                      <div style={{ fontWeight: 700 }}>{row.store_name || '-'}</div>
                      <div style={{ color: '#B8B8C5', fontSize: 12 }}>{row.store_email || '-'}</div>
                    </td>
                    <td>{Number(row?.totals?.completed_orders_count || 0)}</td>
                    <td>{Number(row?.totals?.completed_own_orders_count || 0)}</td>
                    <td>{Number(row?.totals?.completed_platform_orders_count || 0)}</td>
                    <td>{formatMoney(pendingValue)}</td>
                    <td>{formatMoney(row?.totals?.store_payout_paid || 0)}</td>
                    <td>{formatSchedule(row?.payout_schedule || 'weekly')}</td>
                    <td>
                      <div className="admin-fees-actions">
                        <button
                          className="admin-button secondary"
                          onClick={() => navigate(`/admin/lojas/${row.store_id}?tab=Pedidos&status=completed`)}
                        >
                          Resulmo
                        </button>
                        <button
                          className="admin-button secondary"
                          onClick={() => navigate(`/admin/lojas/${row.store_id}?tab=Financeiro`)}
                        >
                          Ver financeiro
                        </button>
                        <button
                          className="admin-button"
                          onClick={() => handleSettleStore(row)}
                          disabled={settling || pendingValue <= 0}
                        >
                          {settling ? 'Processando...' : 'Marcar repasse'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!storesPayouts.length && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', color: '#B8B8C5' }}>
                    Nenhuma loja encontrada para o filtro atual.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
